import { Button, Card, Space, Table, Typography, Input, Select, Col, Row, Tooltip, Dropdown, Menu } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoodsReceiptService } from '../../../Services/Manage/GoodsReceiptService';
import { UserService } from '../../../Services/Manage/UserService';
import {
    EditOutlined, EyeOutlined, InboxOutlined, ClockCircleOutlined,
    CheckCircleOutlined, SyncOutlined, RollbackOutlined,
    DownloadOutlined, PlusOutlined, FilterOutlined, CalendarOutlined, DownOutlined,
} from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const PERMISSION_LINK = '/inventory/goods-receipts';

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false, view_all: false };
    } catch { return { add: false, edit: false, delete: false, read: false, view_all: false }; }
};

const STATUS_COLORS = {
    "Pending": "#1677ff",
    "Partial Received": "#faad14",
    "Received": "#52c41a",
    "Returned": "#f5222d",
};

const StatCard = ({ title, value, sub, color, bg, icon }) => (
    <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
                {sub && <div style={{ fontSize: 10, color: '#1677ff', marginTop: 3, cursor: 'pointer' }}>{sub}</div>}
            </div>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
                {icon}
            </div>
        </div>
    </Card>
);

const GoodsReceiptsDashboard = () => {
    const perm = getPermission(PERMISSION_LINK);
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;

    const [allGRs, setAllGRs] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [loading, setLoading] = useState(false);
    const [userMap, setUserMap] = useState({});
    const [dateFilterRange, setDateFilterRange] = useState(null); // null = All Time (default)
    const [dateFilterLabel, setDateFilterLabel] = useState('All Time');

    const getAllGRs = async () => {
        setLoading(true);
        try {
            const request = await GoodsReceiptService.getAllGR();
            setAllGRs(request.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getUserMap = async () => {
        try {
            const request = await UserService.getAllUsers();
            const map = {};
            (request.data || []).forEach((u) => {
                if (u.user_id) map[String(u.user_id)] = u.name;
            });
            setUserMap(map);
        } catch (error) { console.error(error); }
    };

    useEffect(() => {
        getAllGRs();
        getUserMap();
    }, []);

const visibleGRs = useMemo(
        () => perm.view_all ? allGRs : allGRs.filter((s) => String(s.added_by) === String(currentUserId)),
        [allGRs, perm.view_all, currentUserId]
    );

    // Date-filtered base for stat cards/charts — reacts to the "More Actions"
    // date preset only, not search/status/supplier.
    const dateFilteredGRs = useMemo(() => {
        if (!dateFilterRange) return visibleGRs;
        return visibleGRs.filter((s) => {
            const d = dayjs(s.created_at);
            return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
        });
    }, [visibleGRs, dateFilterRange]);

    const filteredGRs = useMemo(() => {
        let filtered = visibleGRs;
        if (searchText) {
            filtered = filtered.filter((s) =>
                Object.values(s).some((value) => value?.toString().toLowerCase().includes(searchText.toLowerCase()))
            );
        }
        if (selectedSupplier) filtered = filtered.filter((s) => s.supplier_name === selectedSupplier);
        if (selectedStatus) filtered = filtered.filter((s) => s.status === selectedStatus);
        if (dateFilterRange) {
            filtered = filtered.filter((s) => {
                const d = dayjs(s.created_at);
                return d.isValid() && d.isSameOrAfter(dateFilterRange[0], 'day') && d.isSameOrBefore(dateFilterRange[1], 'day');
            });
        }
        return filtered;
    }, [visibleGRs, searchText, selectedStatus, selectedSupplier, dateFilterRange]);

  const totalGRs = visibleGRs.length; // always all-time
    const receivedCount = useMemo(() => dateFilteredGRs.filter((s) => s.status === "Received").length, [dateFilteredGRs]);
    const partialCount = useMemo(() => dateFilteredGRs.filter((s) => s.status === "Partial Received").length, [dateFilteredGRs]);
    const pendingCount = useMemo(() => dateFilteredGRs.filter((s) => s.status === "Pending").length, [dateFilteredGRs]);
    const returnedCount = useMemo(() => dateFilteredGRs.filter((s) => s.status === "Returned").length, [dateFilteredGRs]);

    const statusSummaryData = useMemo(() => {
        const counts = { "Received": receivedCount, "Partial Received": partialCount, "Pending": pendingCount, "Returned": returnedCount };
        return Object.entries(counts).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value, color: STATUS_COLORS[name] }));
    }, [receivedCount, partialCount, pendingCount, returnedCount]);
    const statusSummaryTotal = statusSummaryData.reduce((a, b) => a + b.value, 0);

    const thisMonthStats = useMemo(() => {
        const now = new Date();
        const monthGRs = visibleGRs.filter((s) => {
            const dateStr = s.receipt_date || s.created_at || s.updated_at;
            if (!dateStr) return false;
            const d = new Date(dateStr);
            if (isNaN(d)) return false;
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        });
        return {
            total: monthGRs.length,
            receivedQty: monthGRs.reduce((sum, s) => sum + Number(s.received_qty_total || 0), 0),
            totalItems: monthGRs.reduce((sum, s) => sum + Number(s.total_items || 0), 0),
            totalValue: monthGRs.reduce((sum, s) => sum + Number(s.total_amount || 0), 0),
        };
    }, [visibleGRs]);

const topSuppliersData = useMemo(() => {
        const map = {};
        dateFilteredGRs.forEach((s) => {
            const key = s.supplier_name || "Unknown";
            map[key] = (map[key] || 0) + Number(s.total_amount || 0);
        });
        return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 5);
    }, [dateFilteredGRs]);

    const recentGRs = useMemo(
        () => dateFilteredGRs.slice().sort((a, b) => new Date(b.receipt_date) - new Date(a.receipt_date)).slice(0, 5),
        [dateFilteredGRs]
    );

    const supplierOptions = [...new Set(visibleGRs.map((s) => s.supplier_name).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const statusOptions = ["Pending", "Partial Received", "Received", "Returned"].map((s) => ({ label: s, value: s }));

    const REPORT_COLUMNS = [
        { label: "GR Number" },
        { label: "PO Number" },
        { label: "Supplier" },
        { label: "Receipt Date" },
        { label: "Warehouse" },
        { label: "Total Items" },
        { label: "Received Qty" },
        { label: "Status" },
        { label: "Created By" },
    ];

    const buildReportRows = () => filteredGRs.map((r) => ({
        "GR Number": r.gr_number || "-",
        "PO Number": r.po_number || "-",
        "Supplier": r.supplier_name || "-",
        "Receipt Date": r.receipt_date ? new Date(r.receipt_date).toLocaleDateString('en-GB') : '-',
        "Warehouse": r.warehouse || "-",
        "Total Items": r.total_items ?? 0,
        "Received Qty": Number(r.received_qty_total ?? 0),
        "Status": r.status || "-",
        "Created By": userMap[String(r.added_by)] || r.added_by || '-',
    }));

    const dateStamp = () => dayjs().format("YYYY-MM-DD");

    const handleExportExcel = () => {
        const rows = buildReportRows();
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Goods Receipts Report");
        XLSX.writeFile(wb, `Goods-Receipts-Report-${dateStamp()}.xlsx`);
    };

    const handleExportPDF = () => {
        const rows = buildReportRows();
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("Goods Receipts Report", 14, 14);
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, 20);
        autoTable(doc, {
            startY: 26,
            head: [REPORT_COLUMNS.map((c) => c.label)],
            body: rows.map((r) => REPORT_COLUMNS.map((c) => r[c.label])),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [22, 88, 180] },
        });
        doc.save(`Goods-Receipts-Report-${dateStamp()}.pdf`);
    };

    const exportMenuItems = [
        { key: "excel", label: "Download as Excel (.xlsx)", onClick: handleExportExcel },
        { key: "pdf", label: "Download as PDF", onClick: handleExportPDF },
    ];

    const columns = [
        { title: '#', dataIndex: 'key', width: '4%', align: 'center', key: 'key', render: (_, __, index) => index + 1 },
        { title: 'GR Number', dataIndex: 'gr_number', key: 'gr_number' },
        { title: 'PO Number', dataIndex: 'po_number', key: 'po_number', render: (v) => v || '-' },
        { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', render: (v) => v || '-' },
        { title: 'Receipt Date', dataIndex: 'receipt_date', key: 'receipt_date', render: (v) => v ? new Date(v).toLocaleDateString('en-GB') : '-' },
        { title: 'Warehouse', dataIndex: 'warehouse', key: 'warehouse', render: (v) => v || '-' },
        {
            title: 'Total Items',
            key: 'total_items',
            align: 'center',
            render: (_, record) => record.total_items ?? 0,
        },
        {
            title: 'Received Qty',
            key: 'received_qty',
            align: 'center',
            render: (_, record) => Number(record.received_qty_total ?? 0),
        },
        {
            title: 'Status', key: 'status',
            render: (_, record) => (
                <span style={{
                    background: `${STATUS_COLORS[record.status] || '#8c8c8c'}1a`,
                    color: STATUS_COLORS[record.status] || '#8c8c8c',
                    padding: '3px 10px', borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>
                    {record.status || '-'}
                </span>
            ),
        },
        {
            title: 'Created By',
            dataIndex: 'added_by',
            key: 'added_by',
            render: (v) => userMap[String(v)] || v || '',
        },
        {
            title: 'Action',
            key: 'Action',
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title={!perm.read ? "You don't have permission to view" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/goods-receipts/view-goods-receipt', { state: { id: record.id, action: 'View', enableEdit: false, from: '/inventory/goods-receipts' } })}
                            icon={<EyeOutlined />} size="middle" disabled={!perm.read}
                        />
                    </Tooltip>
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button
                            onClick={() => navigate('/inventory/goods-receipts/edit-goods-receipt', { state: { id: record.id, action: 'Edit', enableEdit: true, from: '/inventory/goods-receipts' } })}
                            icon={<EditOutlined />} size="middle" disabled={!perm.edit}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Goods Receipts</Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Inventory Management &gt; Goods Receipts</Typography.Text>
                </div>
                <Space wrap>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button size="middle" icon={<DownloadOutlined />}>Export</Button>
                    </Dropdown>
                    <Dropdown
                        trigger={['click']}
                        overlay={
                            <Menu
                                onClick={({ key }) => {
                                    let range = null;
                                    let label = 'All Time';
                                    if (key === 'today') {
                                        range = [dayjs().startOf('day'), dayjs().endOf('day')];
                                        label = 'Today';
                                    } else if (key === 'last7') {
                                        range = [dayjs().subtract(6, 'day').startOf('day'), dayjs()];
                                        label = 'Last 7 Days';
                                    } else if (key === 'last30') {
                                        range = [dayjs().subtract(29, 'day').startOf('day'), dayjs()];
                                        label = 'Last 30 Days';
                                    } else if (key === 'lastMonth') {
                                        range = [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')];
                                        label = 'Last Month';
                                    } else if (key === 'thisYear') {
                                        range = [dayjs().startOf('year'), dayjs()];
                                        label = 'This Year';
                                    } else if (key === 'all') {
                                        range = null;
                                        label = 'All Time';
                                    }
                                    setDateFilterRange(range);
                                    setDateFilterLabel(label);
                                }}
                                items={[
                                    { key: 'all', label: <span><CalendarOutlined style={{ marginRight: 6 }} />All Time</span> },
                                    { key: 'today', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Today</span> },
                                    { key: 'last7', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 7 Days</span> },
                                    { key: 'last30', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last 30 Days</span> },
                                    { key: 'lastMonth', label: <span><CalendarOutlined style={{ marginRight: 6 }} />Last Month</span> },
                                    { key: 'thisYear', label: <span><CalendarOutlined style={{ marginRight: 6 }} />This Year</span> },
                                ]}
                            />
                        }
                    >
                        <Button size="middle" icon={<DownOutlined />}>
                            More Actions ({dateFilterLabel})
                        </Button>
                    </Dropdown>
                    <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                        <Button
                            size="middle" type="primary" disabled={!perm.add}
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/inventory/goods-receipts/add-goods-receipt', { state: { action: 'Add', enableEdit: true, from: '/inventory/goods-receipts' } })}
                            style={{ background: '#1677ff', borderColor: '#1677ff' }}
                        >
                            New Goods Receipt
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            <div style={{ padding: '8px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Total GRs" value={totalGRs} sub="View all receipts" color="#722ed1" bg="#f5eeff" icon={<InboxOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Received" value={receivedCount} sub="View received" color="#52c41a" bg="#f6ffed" icon={<CheckCircleOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Partial Received" value={partialCount} sub="View partial" color="#faad14" bg="#fffbe6" icon={<SyncOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Pending" value={pendingCount} sub="View pending" color="#1677ff" bg="#f0f7ff" icon={<ClockCircleOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <StatCard title="Returned" value={returnedCount} sub="View returned" color="#f5222d" bg="#fff1f0" icon={<RollbackOutlined />} />
                    </Col>
                </Row>
            </div>

            <div style={{ padding: '6px 16px 16px' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} lg={17}>
                        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Row gutter={[16, 16]} align="bottom">
                                <Col xs={24} sm={12} md={7}>
                                    <Input
                                        allowClear
                                        placeholder="Search GR number, PO number, supplier..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={5}>
                                    <Select allowClear placeholder="All Status" options={statusOptions}
                                        value={selectedStatus} onChange={setSelectedStatus} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={5}>
                                    <Select allowClear placeholder="All Suppliers" options={supplierOptions}
                                        value={selectedSupplier} onChange={setSelectedSupplier} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <Button icon={<FilterOutlined />} style={{ width: "100%" }}>More Filters</Button>
                                </Col>
                            </Row>
                        </Card>

                        <Table
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                            rowKey="id"
                            dataSource={filteredGRs.slice().sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))}
                            columns={columns}
                        />
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>GR Status Overview</Typography.Text>
                            {statusSummaryTotal === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>📦</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                                    <div style={{ width: 110, height: 110, flexShrink: 0, position: 'relative' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(v, n) => [`${v} (${statusSummaryTotal ? Math.round((v / statusSummaryTotal) * 100) : 0}%)`, n]} />
                                                <Pie data={statusSummaryData} dataKey="value" nameKey="name" innerRadius={34} outerRadius={52} paddingAngle={2}>
                                                    {statusSummaryData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: '#333', lineHeight: 1 }}>{statusSummaryTotal}</div>
                                            <div style={{ fontSize: 9, color: '#888' }}>Total GRs</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {statusSummaryData.map((e, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ color: e.color, fontSize: 14 }}>●</span>
                                                    <span style={{ color: '#333' }}>{e.name}</span>
                                                </span>
                                                <span style={{ fontWeight: 700, color: '#333' }}>{e.value} ({statusSummaryTotal ? Math.round((e.value / statusSummaryTotal) * 100) : 0}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>This Month Summary</Typography.Text>
                            <div style={{ marginTop: 10 }}>
                                {[
                                    ["Total GRs", thisMonthStats.total],
                                    ["Received Qty", thisMonthStats.receivedQty],
                                    ["Total Items", thisMonthStats.totalItems],
                                    ["Total Value", `₹ ${thisMonthStats.totalValue.toLocaleString('en-IN')}`],
                                ].map(([label, value], i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                                        <Typography.Text type="secondary">{label}</Typography.Text>
                                        <Typography.Text strong>{value}</Typography.Text>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Top Suppliers (By Received Value)</Typography.Text>
                            {topSuppliersData.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>🏭</span>
                                    <span style={{ marginTop: 6 }}>No data yet</span>
                                </div>
                            ) : (
                                <div style={{ marginTop: 12 }}>
                                    {topSuppliersData.map((s, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 10 }}>
                                            <span style={{ color: '#333' }}>{i + 1}. {s.name}</span>
                                            <span style={{ fontWeight: 700, color: '#1658b4' }}>₹ {s.value.toLocaleString('en-IN')}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>

                        <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Recent GRs</Typography.Text>
                            <div style={{ marginTop: 12 }}>
                                {recentGRs.map((g, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                                        <span style={{ color: '#1677ff', cursor: 'pointer' }}
                                            onClick={() => navigate('/inventory/goods-receipts/view-goods-receipt', { state: { id: g.id, action: 'View', enableEdit: false, from: '/inventory/goods-receipts' } })}
                                        >{g.gr_number}</span>
                                        <span style={{ color: '#888' }}>{g.receipt_date ? new Date(g.receipt_date).toLocaleDateString('en-GB') : '-'}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default GoodsReceiptsDashboard;